import {
  FieldType,
  OptionSourceType,
  SelectFieldDisplayComponent,
} from 'api/models/FieldTemplate';
import { useTranslation } from 'react-i18next';
import { NumberField, SelectField, StringField } from './Fields';
import { DateField } from './Fields/DateField';
import { useFormik } from 'formik';
import OptionAutocompleteField from './Fields/OptionAutocompleteField';
import RadioField from './Fields/RadioField';
import CalculatedField from './Fields/CalculatedField';
import { getFormValidationSchema, getInitialFormValues } from 'forms/Form';
import { useRecoilValue } from 'recoil';
import { fieldByIdSelector } from 'state';

interface FieldRendererProps {
  fieldId: string;
  disabled?: boolean;
}

function FieldRenderer({ fieldId, disabled }: FieldRendererProps) {
  const field = useRecoilValue(fieldByIdSelector(fieldId));

  // this formik instance is used only to test values in fields in the WYSIWYG form editor page
  // this is not going to save the edited form, it's just for testing purposes
  // the saving of the form template is done in the FormEditorControls component
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialFormValues([field!]),
    validationSchema: getFormValidationSchema({
      templates: [field!],
    }),
    onSubmit: _values => {},
  });

  const { t } = useTranslation();
  switch (field?.fieldType) {
    case FieldType.Number:
      return (
        <NumberField
          disabled={disabled}
          field={field}
          value={formik.values[field.id]}
          name={field.id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
        />
      );
    case FieldType.Text:
      return (
        <StringField
          disabled={disabled}
          field={field}
          value={formik.values[field.id] ?? field.fieldAttributes.defaultValue}
          name={field.id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
        />
      );
    case FieldType.Date:
      return (
        <DateField
          disabled={disabled}
          field={field}
          value={
            formik.values[field.id] === '' ? null : formik.values[field.id]
          }
          defaultValue={field.fieldAttributes.defaultValue ?? null}
          name={field.id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
        />
      );
    case FieldType.Select:
      return field.fieldAttributes.optionsSourceType ===
        OptionSourceType.CodeLibrary ? (
        <OptionAutocompleteField
          onChange={(value: string | string[]) =>
            formik.setFieldValue(field.id, value)
          }
          value={formik.values[field.id] as string}
          defaultValue={
            field.fieldAttributes.defaultValue as string | string[] | null
          }
          isMultiSelect={field.fieldAttributes.isMultiSelect ?? false}
          optionsSourceCodeLibraryId={
            field.fieldAttributes.optionsSourceCodeLibraryId
          }
          label={field.fieldAttributes.label}
          isRequired={field.fieldAttributes.isRequired ?? false}
          sx={{ mt: 1 }}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          description={field.fieldAttributes.description}
        />
      ) : field.fieldAttributes.component ===
        SelectFieldDisplayComponent.Dropdown ? (
        <SelectField
          disabled={disabled}
          field={field}
          value={
            field.fieldAttributes.isMultiSelect
              ? formik.values[field.id] ??
                field.fieldAttributes.defaultValue ??
                []
              : formik.values[field.id] ??
                field.fieldAttributes.defaultValue ??
                ''
          }
          name={field.id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          onClear={() => {
            formik.setFieldValue(
              field.id,
              field.fieldAttributes.isMultiSelect ? [] : '',
            );
          }}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
        />
      ) : (
        <RadioField
          disabled={disabled}
          field={field}
          value={formik.values[field.id] ?? field.fieldAttributes.defaultValue}
          name={field.id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
        />
      );
    case FieldType.Calculated:
      return (
        <CalculatedField
          disabled={disabled}
          field={field}
          error={Boolean(formik.errors[field.id])}
          helperText={formik.errors[field.id]}
          name={field.id}
          fullWidth
          label={t(`forms.fieldType.${field.fieldType}`)}
          onChange={() => undefined}
        />
      );
    default:
      throw new Error(`Field type ${field?.fieldType} not supported`);
  }
}

export default FieldRenderer;
